import {
  logEvent as firebaseLogEvent,
  setUserId,
  setUserProperties,
} from "firebase/analytics";

import { analyticsReady, IS_ANALYTICS_DEBUG } from "../firebase";
// Default import, not `{ version }` — webpack treats JSON as default-exporting
// and warns that named imports from it are going away.
import packageJson from "../../package.json";
import { ANALYTICS_EVENTS, NOT_SET, buildMenuTabClickedEvent } from "./events";
import { getScreenName } from "./screenNames";

export { ANALYTICS_EVENTS };
export * from "./events";

// GA4 hard limits. Exceeding them does not error, it silently drops the
// offending parameter or truncates the value, so we clamp deliberately instead.
const MAX_EVENT_PARAMS = 25;
const MAX_PARAM_VALUE_LENGTH = 100;
const MAX_USER_PROPERTY_LENGTH = 36;

// Same switch firebase.js uses to turn on the gtag-level debug_mode, kept in one
// place so console logging and DebugView routing can never disagree.
const isDev = IS_ANALYTICS_DEBUG;

/* -------------------------------------------------- readiness + event buffer */

let instance = null;
let settled = false;


const pending = [];
const MAX_PENDING = 50;

analyticsReady.then((resolved) => {
  settled = true;
  instance = resolved;

  const queued = pending.splice(0, pending.length);
  if (!resolved) return;
  queued.forEach((run) => run(resolved));
});

const withAnalytics = (run) => {
  if (settled) {
    if (instance) run(instance);
    return;
  }
  if (pending.length < MAX_PENDING) pending.push(run);
};

/* ------------------------------------------------------------- normalisation */

const truncate = (value, limit) =>
  value.length > limit ? value.slice(0, limit) : value;


const normalise = (value, limit) => {
  if (value === null || value === undefined || value === "") return NOT_SET;
  if (typeof value === "number") return Number.isFinite(value) ? value : NOT_SET;
  if (typeof value === "boolean") return value;
  return truncate(String(value), limit);
};

const buildParams = (params = {}) => {
  const entries = Object.entries(params);

  if (isDev && entries.length > MAX_EVENT_PARAMS) {
    console.warn(
      `[analytics] ${entries.length} params exceeds the GA4 cap of ${MAX_EVENT_PARAMS}; the excess will be dropped.`
    );
  }

  const payload = entries
    .slice(0, MAX_EVENT_PARAMS)
    .reduce((accumulator, [key, value]) => {
      accumulator[key] = normalise(value, MAX_PARAM_VALUE_LENGTH);
      return accumulator;
    }, {});

  if (isDev) payload.debug_mode = true;

  return payload;
};

/* -------------------------------------------------------------------- events */

export const logAnalyticsEvent = (eventName, params = {}) => {
  if (!eventName) return;

  const payload = buildParams(params);

  if (isDev) console.debug("[analytics]", eventName, payload);

  withAnalytics((analytics) => {
    try {
      firebaseLogEvent(analytics, eventName, payload);
    } catch (error) {
      // Never let instrumentation break the flow it is measuring.
      console.error("[analytics] failed to log", eventName, error);
    }
  });
};

/* ---------------------------------------------------------------- identity */


export const setAnalyticsUser = (user) => {
  if (!user) return;

  const databaseId = user._id;

  withAnalytics((analytics) => {
    try {
      if (databaseId) setUserId(analytics, String(databaseId));

      setUserProperties(analytics, {
        database_id: normalise(databaseId, MAX_USER_PROPERTY_LENGTH),
        virtual_id: normalise(user.virtualId, MAX_USER_PROPERTY_LENGTH),
        business_id: normalise(user.businessId, MAX_USER_PROPERTY_LENGTH),
        business_name: normalise(user.businessName, MAX_USER_PROPERTY_LENGTH),
        user_type: normalise(user.userType, MAX_USER_PROPERTY_LENGTH),
        platform: "web",
        app_version: normalise(packageJson.version, MAX_USER_PROPERTY_LENGTH),
      });
    } catch (error) {
      console.error("[analytics] failed to set user properties", error);
    }
  });
};


export const isFirstLogin = (user) => Number(user?.stage || 0) > 0;

export const clearAnalyticsUser = () => {
  withAnalytics((analytics) => {
    try {
      setUserId(analytics, null);
      setUserProperties(analytics, {
        database_id: NOT_SET,
        virtual_id: NOT_SET,
        business_id: NOT_SET,
        business_name: NOT_SET,
        user_type: NOT_SET,
      });
    } catch (error) {
      console.error("[analytics] failed to clear user properties", error);
    }
  });
};

/* ------------------------------------------------------------- screen views */

let previousScreen = null;

export const trackScreenView = (pathname) => {
  const screenName = getScreenName(pathname);

  // Re-rendering the same route (a hash change on the reports list, a filter
  // reset) must not look like navigation.
  if (screenName === previousScreen) return;

  logAnalyticsEvent(ANALYTICS_EVENTS.SCREEN_VIEW, {
    firebase_screen: screenName,
    firebase_screen_class: screenName,
    firebase_previous_screen: previousScreen,
    screen_name: screenName,
    previous_screen: previousScreen,
  });

  previousScreen = screenName;
};

export const resetScreenTracking = () => {
  previousScreen = null;
};

/* --------------------------------------------------------------- menu nav */

// Menu titles repeat across role variants (e.g. two "Referred Cases" entries
// in menuConfig), so re-firing for the same tab on every click is expected —
// dedupe only within a click, not across them.
export const trackMenuTabClicked = (tabName, tabPath) => {
  const eventName = buildMenuTabClickedEvent(tabName);
  if (!eventName) return;

  logAnalyticsEvent(eventName, {
    tab_name: tabName,
    tab_path: tabPath,
  });
};
